import * as Sentry from '@sentry/react';
import type { Contexts, Primitive } from '@sentry/types';

const ignoreErrors: RegExp[] = [
  /Cannot read property 'getReadModeExtract' of undefined/i,
  /Cannot read property 'getReadModeRender' of undefined/i,
  /Cannot read property 'getReadModeConfig' of undefined/i,
  /ibFindAllVideos is not defined/i,
  //Outlook Exception Doc: https://www.notion.so/porter-logistics/Suppression-of-UnhandledRejection-related-fatal-errors-on-Sentry-646503d5ce084dc7a784889c8b2c086e
  /Non-Error promise rejection captured with value/i,
];

interface InitOptions {
  dsn: string;
  env: string;
  release?: string;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
}

export interface SentryTags {
  [key: string]: Primitive;
}

export function init(initOptions: InitOptions): void {
  Sentry.init({
    dsn: initOptions.dsn,
    environment: initOptions.env,
    release: initOptions.release,
    tracesSampleRate: initOptions.tracesSampleRate,
    replaysSessionSampleRate: initOptions.replaysSessionSampleRate,
    replaysOnErrorSampleRate: initOptions.replaysOnErrorSampleRate,
    ignoreErrors,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
  });
}

export function captureException(str: string): void {
  Sentry.captureException(new Error(str));
}

export function captureExceptionWithContext(str: string, obj: Contexts): void {
  Sentry.captureException(new Error(str), {
    contexts: obj,
  });
}

export function captureExceptionWithOptions(
  str: string,
  tags?: SentryTags,
  contexts?: Contexts,
): void {
  Sentry.captureException(new Error(str), {
    tags: tags,
    contexts: contexts,
  });
}

export function setTag(tag_key: string, tag_val: string): void {
  Sentry.setTag(tag_key, tag_val);
}

export function setContext(
  context_str: string,
  context_obj: Record<string, unknown>,
): void {
  Sentry.setContext(context_str, context_obj);
}

export function captureMessage(str: string): void {
  Sentry.captureMessage(str);
}

export function captureMessageWithOptions(
  message: string,
  tags?: SentryTags,
  contexts?: Contexts,
): void {
  Sentry.captureMessage(message, {
    tags: tags,
    contexts: contexts,
  });
}

export function captureMessageWithContext(str: string, obj: Contexts): void {
  Sentry.captureMessage(str, {
    contexts: obj,
  });
}

export function capturePromiseError(error: Error): void {
  Sentry.captureException(error);
}

export function capturePromiseErrorWithContext(
  error: Error,
  obj: Contexts,
): void {
  Sentry.captureException(error, {
    contexts: obj,
  });
}

export function withProfiler<T extends React.ComponentType<any>>(
  cmp: T,
): React.ComponentType<any> {
  return Sentry.withProfiler(cmp);
}
